import React, { useState, useEffect } from 'react';
import {
    Card,
    Form,
    Input,
    Select,
    InputNumber,
    DatePicker,
    TimePicker,
    Button,
    Row,
    Col,
    message,
    Typography
} from 'antd';
import {
    SaveOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';

const { Option } = Select;
const { Title } = Typography;

const WorkLogEntry = () => {
    const [form] = Form.useForm();
    const [blocks, setBlocks] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [blocksRes, equipmentRes] = await Promise.all([
                api.get('/blocks'),
                api.get('/equipment/available')
            ]);
            setBlocks(blocksRes.data);
            setEquipment(equipmentRes.data);
        } catch (error) {
            message.error('Failed to load initial data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                ...values,
                start_time: moment(values.start_date + ' ' + values.start_time).toISOString(),
                end_time: moment(values.start_date + ' ' + values.end_time).toISOString()
            };
            
            await api.post('/worklogs', payload);
            message.success('Work log recorded successfully');
            form.resetFields();
            fetchInitialData(); // Refresh available equipment
        } catch (error) {
            message.error('Failed to record work log');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card
            title={<Title level={4}>Record Work Activity</Title>}
            loading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="block_id"
                            label="Mining Block"
                            rules={[{ required: true, message: 'Please select a block' }]}
                        >
                            <Select placeholder="Select mining block">
                                {blocks.map(block => (
                                    <Option key={block.id} value={block.id}>
                                        {block.block_code} - {block.block_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="equipment_id"
                            label="Equipment"
                            rules={[{ required: true, message: 'Please select equipment' }]}
                        >
                            <Select placeholder="Select equipment">
                                {equipment.map(eq => (
                                    <Option key={eq.id} value={eq.id}>
                                        {eq.equipment_code} - {eq.type_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="material_type"
                            label="Material Type"
                            rules={[{ required: true, message: 'Please select material type' }]}
                        >
                            <Select placeholder="Select material">
                                <Option value="soft">Soft Soil</Option>
                                <Option value="hard">Hard Soil</Option>
                                <Option value="coal">Coal</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="quantity"
                            label="Quantity"
                            rules={[{ required: true, message: 'Please enter quantity' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={10}
                                placeholder="Enter quantity"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="unit"
                            label="Unit"
                            initialValue="m3"
                        >
                            <Select>
                                <Option value="m3">Cubic Meters (m³)</Option>
                                <Option value="tons">Tons</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="start_date"
                            label="Work Date"
                            rules={[{ required: true, message: 'Please select date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="start_time"
                            label="Start Time"
                            rules={[{ required: true, message: 'Please select start time' }]}
                        >
                            <TimePicker 
                                style={{ width: '100%' }} 
                                format="HH:mm"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="end_time"
                            label="End Time"
                            rules={[{ required: true, message: 'Please select end time' }]}
                        >
                            <TimePicker 
                                style={{ width: '100%' }} 
                                format="HH:mm"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="notes"
                    label="Notes"
                >
                    <Input.TextArea 
                        rows={3} 
                        placeholder="Enter any additional notes or observations..."
                    />
                </Form.Item>

                <Row justify="space-between">
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => form.resetFields()}
                    >
                        Reset
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                    >
                        Save Work Log
                    </Button>
                </Row>
            </Form>
        </Card>
    );
};

export default WorkLogEntry;